import { Suspense, useRef } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { Float, OrbitControls } from '@react-three/drei'
import * as THREE from 'three'

// ---------------------------------------------------------------------------
// Matériaux partagés
// ---------------------------------------------------------------------------

const BODY_COLOR = '#171f33'
const BODY_EMISSIVE = '#00101a'
const CYAN = '#00dbe9'
const VIOLET = '#ebb2ff'
const ACCENT = '#00f0ff'

function bodyMat(emissiveIntensity = 0.06) {
  return (
    <meshStandardMaterial
      color={BODY_COLOR}
      emissive={BODY_EMISSIVE}
      emissiveIntensity={emissiveIntensity}
      metalness={0.85}
      roughness={0.18}
    />
  )
}

function accentMat(color = CYAN, intensity = 0.9) {
  return (
    <meshStandardMaterial
      color={color}
      emissive={color}
      emissiveIntensity={intensity}
      metalness={0.3}
      roughness={0.05}
    />
  )
}

// ---------------------------------------------------------------------------
// Anneaux orbitaux
// ---------------------------------------------------------------------------

function OrbitalRings() {
  const ring1 = useRef<THREE.Mesh>(null)
  const ring2 = useRef<THREE.Mesh>(null)

  useFrame((state) => {
    const t = state.clock.elapsedTime
    if (ring1.current) {
      ring1.current.rotation.x = t * 0.4
      ring1.current.rotation.z = t * 0.15
    }
    if (ring2.current) {
      ring2.current.rotation.y = t * 0.5
      ring2.current.rotation.z = -t * 0.2
    }
  })

  return (
    <group>
      <mesh ref={ring1} rotation={[Math.PI / 2.8, 0, 0.4]}>
        <torusGeometry args={[1.55, 0.012, 8, 80]} />
        <meshStandardMaterial color={CYAN} emissive={CYAN} emissiveIntensity={0.6} transparent opacity={0.55} />
      </mesh>
      <mesh ref={ring2} rotation={[Math.PI / 5, 0.5, 0]}>
        <torusGeometry args={[1.82, 0.008, 8, 80]} />
        <meshStandardMaterial color={VIOLET} emissive={VIOLET} emissiveIntensity={0.5} transparent opacity={0.4} />
      </mesh>
    </group>
  )
}

// ---------------------------------------------------------------------------
// Corps du robot (géométries primitives)
// ---------------------------------------------------------------------------

function RobotMesh() {
  const scanRef = useRef<THREE.Mesh>(null)
  const eyeLeftRef = useRef<THREE.Mesh>(null)
  const eyeRightRef = useRef<THREE.Mesh>(null)
  const chestCoreRef = useRef<THREE.Mesh>(null)

  useFrame((state) => {
    const t = state.clock.elapsedTime
    // Clignottement des yeux
    const blink = Math.sin(t * 1.2) * 0.5 + 0.5
    if (eyeLeftRef.current) {
      ;(eyeLeftRef.current.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.5 + blink * 0.5
    }
    if (eyeRightRef.current) {
      ;(eyeRightRef.current.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.5 + blink * 0.5
    }
    // Pulsation core
    if (chestCoreRef.current) {
      ;(chestCoreRef.current.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.4 + Math.sin(t * 2) * 0.3
    }
    // Scan line descend et remonte
    if (scanRef.current) {
      scanRef.current.position.y = Math.sin(t * 0.8) * 0.5
    }
  })

  return (
    <group>
      {/* ── Tête ── */}
      <mesh position={[0, 1.58, 0]}>
        <boxGeometry args={[0.62, 0.52, 0.52]} />
        {bodyMat(0.08)}
      </mesh>
      {/* Visière */}
      <mesh position={[0, 1.6, 0.26]}>
        <boxGeometry args={[0.5, 0.22, 0.04]} />
        <meshStandardMaterial color="#0b1326" emissive={CYAN} emissiveIntensity={0.15} metalness={0.2} roughness={0.8} transparent opacity={0.7} />
      </mesh>
      {/* Œil gauche */}
      <mesh ref={eyeLeftRef} position={[-0.15, 1.64, 0.27]}>
        <sphereGeometry args={[0.055, 16, 16]} />
        <meshStandardMaterial color={CYAN} emissive={CYAN} emissiveIntensity={1} />
      </mesh>
      {/* Œil droit */}
      <mesh ref={eyeRightRef} position={[0.15, 1.64, 0.27]}>
        <sphereGeometry args={[0.055, 16, 16]} />
        <meshStandardMaterial color={CYAN} emissive={CYAN} emissiveIntensity={1} />
      </mesh>
      {/* Antenne */}
      <mesh position={[0, 1.9, 0]}>
        <cylinderGeometry args={[0.012, 0.012, 0.22, 8]} />
        {accentMat(CYAN, 0.5)}
      </mesh>
      <mesh position={[0, 2.02, 0]}>
        <sphereGeometry args={[0.03, 12, 12]} />
        {accentMat(CYAN, 1.2)}
      </mesh>

      {/* ── Cou ── */}
      <mesh position={[0, 1.27, 0]}>
        <cylinderGeometry args={[0.12, 0.15, 0.22, 12]} />
        {bodyMat()}
      </mesh>

      {/* ── Torse ── */}
      <mesh position={[0, 0.55, 0]}>
        <boxGeometry args={[1.05, 1.0, 0.58]} />
        {bodyMat(0.05)}
      </mesh>
      {/* Panneau torse avant */}
      <mesh position={[0, 0.62, 0.3]}>
        <boxGeometry args={[0.75, 0.62, 0.04]} />
        <meshStandardMaterial color="#131b2e" emissive={CYAN} emissiveIntensity={0.04} metalness={0.6} roughness={0.4} />
      </mesh>
      {/* Core chest — pulsant */}
      <mesh ref={chestCoreRef} position={[0, 0.68, 0.31]}>
        <sphereGeometry args={[0.1, 20, 20]} />
        <meshStandardMaterial color={VIOLET} emissive={VIOLET} emissiveIntensity={0.8} transparent opacity={0.9} />
      </mesh>
      {/* Barre status gauche */}
      <mesh position={[-0.22, 0.32, 0.31]}>
        <boxGeometry args={[0.28, 0.04, 0.01]} />
        {accentMat(CYAN, 0.6)}
      </mesh>
      {/* Barre status droite */}
      <mesh position={[0.22, 0.32, 0.31]}>
        <boxGeometry args={[0.28, 0.04, 0.01]} />
        {accentMat(VIOLET, 0.5)}
      </mesh>
      {/* Scan line */}
      <mesh ref={scanRef} position={[0, 0.6, 0.32]}>
        <boxGeometry args={[0.7, 0.008, 0.005]} />
        <meshStandardMaterial color={ACCENT} emissive={ACCENT} emissiveIntensity={1.5} transparent opacity={0.6} />
      </mesh>

      {/* ── Épaule gauche ── */}
      <mesh position={[-0.67, 0.95, 0]}>
        <sphereGeometry args={[0.18, 16, 16]} />
        {bodyMat(0.1)}
      </mesh>
      {/* Bras gauche haut */}
      <mesh position={[-0.72, 0.48, 0]}>
        <cylinderGeometry args={[0.1, 0.09, 0.7, 12]} />
        {bodyMat()}
      </mesh>
      {/* Coude gauche */}
      <mesh position={[-0.72, 0.09, 0]}>
        <sphereGeometry args={[0.1, 12, 12]} />
        {bodyMat(0.08)}
      </mesh>
      {/* Avant-bras gauche */}
      <mesh position={[-0.72, -0.28, 0]}>
        <cylinderGeometry args={[0.085, 0.09, 0.55, 12]} />
        {bodyMat()}
      </mesh>
      {/* Main gauche */}
      <mesh position={[-0.72, -0.62, 0]}>
        <boxGeometry args={[0.2, 0.25, 0.14]} />
        {bodyMat()}
      </mesh>

      {/* ── Épaule droite ── */}
      <mesh position={[0.67, 0.95, 0]}>
        <sphereGeometry args={[0.18, 16, 16]} />
        {bodyMat(0.1)}
      </mesh>
      {/* Bras droit haut */}
      <mesh position={[0.72, 0.48, 0]}>
        <cylinderGeometry args={[0.1, 0.09, 0.7, 12]} />
        {bodyMat()}
      </mesh>
      {/* Coude droit */}
      <mesh position={[0.72, 0.09, 0]}>
        <sphereGeometry args={[0.1, 12, 12]} />
        {bodyMat(0.08)}
      </mesh>
      {/* Avant-bras droit */}
      <mesh position={[0.72, -0.28, 0]}>
        <cylinderGeometry args={[0.085, 0.09, 0.55, 12]} />
        {bodyMat()}
      </mesh>
      {/* Main droite */}
      <mesh position={[0.72, -0.62, 0]}>
        <boxGeometry args={[0.2, 0.25, 0.14]} />
        {bodyMat()}
      </mesh>

      {/* ── Hanches ── */}
      <mesh position={[0, -0.1, 0]}>
        <boxGeometry args={[0.9, 0.28, 0.52]} />
        {bodyMat()}
      </mesh>

      {/* ── Jambe gauche ── */}
      <mesh position={[-0.27, -0.62, 0]}>
        <cylinderGeometry args={[0.16, 0.14, 0.72, 12]} />
        {bodyMat()}
      </mesh>
      {/* Genou gauche */}
      <mesh position={[-0.27, -1.0, 0]}>
        <sphereGeometry args={[0.15, 12, 12]} />
        {bodyMat(0.06)}
      </mesh>
      {/* Tibia gauche */}
      <mesh position={[-0.27, -1.38, 0]}>
        <cylinderGeometry args={[0.11, 0.12, 0.62, 12]} />
        {bodyMat()}
      </mesh>
      {/* Pied gauche */}
      <mesh position={[-0.27, -1.74, 0.06]}>
        <boxGeometry args={[0.26, 0.16, 0.44]} />
        {bodyMat()}
      </mesh>

      {/* ── Jambe droite ── */}
      <mesh position={[0.27, -0.62, 0]}>
        <cylinderGeometry args={[0.16, 0.14, 0.72, 12]} />
        {bodyMat()}
      </mesh>
      {/* Genou droit */}
      <mesh position={[0.27, -1.0, 0]}>
        <sphereGeometry args={[0.15, 12, 12]} />
        {bodyMat(0.06)}
      </mesh>
      {/* Tibia droit */}
      <mesh position={[0.27, -1.38, 0]}>
        <cylinderGeometry args={[0.11, 0.12, 0.62, 12]} />
        {bodyMat()}
      </mesh>
      {/* Pied droit */}
      <mesh position={[0.27, -1.74, 0.06]}>
        <boxGeometry args={[0.26, 0.16, 0.44]} />
        {bodyMat()}
      </mesh>
    </group>
  )
}

// ---------------------------------------------------------------------------
// Scène principale
// ---------------------------------------------------------------------------

function RobotScene() {
  return (
    <>
      <ambientLight intensity={0.25} />
      <pointLight position={[3, 4, 3]} intensity={3} color="#00f0ff" />
      <pointLight position={[-3, -2, -2]} intensity={0.8} color="#ebb2ff" />
      <pointLight position={[0, -3, 2]} intensity={0.4} color="#00dbe9" />

      <Float speed={1.2} rotationIntensity={0.08} floatIntensity={0.3}>
        <group position={[0, 0.3, 0]}>
          <RobotMesh />
          <OrbitalRings />
        </group>
      </Float>

      <OrbitControls
        enableZoom={false}
        enablePan={false}
        autoRotate
        autoRotateSpeed={0.4}
        maxPolarAngle={Math.PI / 1.6}
        minPolarAngle={Math.PI / 3}
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export default function Robot3D() {
  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {/* Glow ambiant */}
      <div className="absolute w-[480px] h-[480px] bg-primary-container/8 rounded-full blur-[140px] pointer-events-none" />

      {/* Canvas R3F */}
      <div className="relative z-10 w-full h-full">
        <Canvas
          camera={{ position: [0, 0.3, 4.2], fov: 42 }}
          gl={{ antialias: true, alpha: true }}
          style={{ background: 'transparent' }}
        >
          <Suspense fallback={null}>
            <RobotScene />
          </Suspense>
        </Canvas>
      </div>

      {/* Panel flottant — statut système */}
      <div className="absolute top-[18%] right-[4%] glass-panel p-4 rounded-lg border border-outline-variant/10 w-44 hidden lg:block z-20">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-2 h-2 rounded-full bg-secondary status-pulse" />
          <span className="font-body text-[9px] tracking-widest uppercase text-on-surface-variant">Core System</span>
        </div>
        <div className="h-1 w-full bg-surface-container-highest rounded-full overflow-hidden">
          <div className="h-full w-3/4 bg-secondary" />
        </div>
        <div className="mt-2 font-headline text-xs text-primary-fixed-dim">LOAD: OPTIMAL</div>
      </div>

      {/* Panel flottant — articulation */}
      <div className="absolute bottom-[28%] left-[4%] glass-panel p-4 rounded-lg border border-outline-variant/10 w-52 hidden lg:block z-20">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-2 h-2 rounded-full bg-primary-container status-pulse" />
          <span className="font-body text-[9px] tracking-widest uppercase text-on-surface-variant">Articulation-X</span>
        </div>
        <div className="grid grid-cols-3 gap-1 h-8 items-end">
          <div className="bg-primary-container/40 h-2/3 rounded-sm" />
          <div className="bg-primary-container/40 h-full rounded-sm" />
          <div className="bg-primary-container/40 h-1/2 rounded-sm" />
        </div>
      </div>

      {/* Overlay décoratif SVG */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none opacity-10 z-0"
        viewBox="0 0 400 400"
      >
        <circle cx="200" cy="200" r="185" fill="none" stroke="#00f0ff" strokeWidth="0.5" strokeDasharray="10 6" />
        <line x1="0" y1="200" x2="400" y2="200" stroke="#00f0ff" strokeWidth="0.4" />
        <line x1="200" y1="0" x2="200" y2="400" stroke="#00f0ff" strokeWidth="0.4" />
      </svg>
    </div>
  )
}
